import React, { useState } from 'react';
import { Eye, Target, Heart, Scale, Award, Users, TrendingUp, Landmark, X, ZoomIn } from 'lucide-react';
import { missionData } from '../data/mock';

const MissionSection = () => {
  const [showTheoryModal, setShowTheoryModal] = useState(false);

  const valueIcons = {
    'Equity': Scale,
    'Advocacy': Heart,
    'Excellence': Award,
    'Partnership': Users,
    'Growth & Development': TrendingUp,
    'Legacy': Landmark
  };

  const valueColors = {
    'Equity': { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
    'Advocacy': { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
    'Excellence': { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
    'Partnership': { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200' },
    'Growth & Development': { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
    'Legacy': { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' }
  };

  return (
    <section id="vision" className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal-100/50 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-100/50 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            Our Foundation
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            Vision, Mission, Values & Theory of Change
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            Guiding principles that drive our commitment to early childhood education in Washington, DC.
          </p>
        </div>

        {/* Vision and Mission Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* Vision Card */}
          <div className="group relative">
            <div className="relative bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8 h-full border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute top-0 left-8 right-8 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full" />
              <div className="bg-teal-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Eye className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                {missionData.vision.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {missionData.vision.description}
              </p>
              <div className="absolute bottom-4 right-4 w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 opacity-10 rounded-full group-hover:scale-150 transition-transform duration-500" />
            </div>
          </div>

          {/* Mission Card */}
          <div className="group relative">
            <div className="relative bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 h-full border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute top-0 left-8 right-8 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full" />
              <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                {missionData.mission.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {missionData.mission.description}
              </p>
              <div className="absolute bottom-4 right-4 w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 opacity-10 rounded-full group-hover:scale-150 transition-transform duration-500" />
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">Our Values</h3>
            <p className="text-slate-600">The principles that guide everything we do</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {missionData.values.map((value, index) => {
              const IconComponent = valueIcons[value.title] || Heart;
              const colors = valueColors[value.title] || { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' };
              return (
                <div 
                  key={index}
                  className={`bg-white rounded-2xl p-6 border ${colors.border} shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                >
                  <div className={`${colors.bg} w-12 h-12 rounded-xl flex items-center justify-center mb-4`}>
                    <IconComponent className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <h4 className="text-lg font-bold text-slate-800 mb-2">{value.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Theory of Change Section */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 border border-amber-100 shadow-lg">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">Theory of Change</h3>
            <p className="text-slate-600 max-w-3xl mx-auto">
              Our strategic roadmap for improving infant and toddler education in the District, helping children reach their full potential.
            </p>
          </div>
          
          {/* Theory of Change Preview */}
          <div className="relative bg-white rounded-2xl p-2 shadow-md overflow-hidden">
            <button
              type="button"
              onClick={() => setShowTheoryModal(true)}
              aria-label="View Theory of Change diagram full screen"
              data-testid="theory-of-change-preview"
              className="block w-full focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-xl"
            >
              <img
                src="/theory-of-change.jpg"
                alt="DCELC Theory of Change diagram: If DCELC strengthens awareness, action, and education among diverse stakeholders, then systemic inequities will be diminished, the educator workforce will be adequately supported, and high-quality ECE will be recognized and funded as a public good."
                className="w-full h-auto rounded-xl hover:opacity-95 transition-opacity"
              />
            </button>
            <div className="mt-4 text-center">
              <a 
                href="https://customer-assets.emergentagent.com/job_19451de5-ade2-4c9c-97e0-a11403f5250c/artifacts/jtp1xm3d_DCELC_TheoryofChange_L1%20%281%29.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-full font-semibold hover:bg-amber-600 transition-colors"
              >
                <ZoomIn className="w-5 h-5" />
                Open Full Size
              </a>
            </div>
          </div>

          {/* Three Pillars */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-rose-50 rounded-xl p-5 border border-rose-200">
              <h4 className="font-bold text-rose-700 mb-2">Awareness</h4>
              <p className="text-rose-600 text-sm">Educating and engaging the public and media about ECE</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h4 className="font-bold text-blue-700 mb-2">Action</h4>
              <p className="text-blue-600 text-sm">Direct advocacy, policy change, and systemic improvements</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
              <h4 className="font-bold text-emerald-700 mb-2">Education</h4>
              <p className="text-emerald-600 text-sm">Professional development and support for ECE workforce</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Theory of Change */}
      {showTheoryModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowTheoryModal(false)}
        >
          <div className="relative max-w-6xl w-full max-h-[90vh] overflow-auto bg-white rounded-2xl p-4">
            <button 
              onClick={() => setShowTheoryModal(false)}
              className="absolute top-4 right-4 bg-slate-800 text-white p-2 rounded-full hover:bg-slate-700 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src="/theory-of-change.jpg"
              alt="DCELC Theory of Change diagram, full size"
              className="w-full h-auto rounded-xl"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default MissionSection;
